import { useState } from 'react';
import Textfield from '@atlaskit/textfield';
import SharedButton from 'shared/components/buttons/button';
import DeliveryRequestsApi from 'core/api/delivery-requests/delivery-requests-api.service';
import { ITransportGroupPreviewProblem } from 'core/api/transport-groups/transport-groups-api-interface';

/**
 * Correcting the address that stopped a request being routed.
 *
 * Offered where the problem is, rather than on another screen: the person
 * reading the route is usually the one who knows where the ward is, and making
 * them leave to fix it is how a fixable request becomes a closed one.
 *
 * The site is the only required part because the five known sites resolve from
 * their name alone. Anywhere else needs the address under it, which is exactly
 * what the form asks for.
 */

interface IProps {
  problem: ITransportGroupPreviewProblem;
  /** Re-solves once the address takes, so the route on screen is the real one. */
  onFixed: () => Promise<void>;
  onCancel: () => void;
}

const FixAddress = ({ problem, onFixed, onCancel }: IProps) => {
  const [site, setSite] = useState('');
  const [location, setLocation] = useState('');
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!site.trim()) {
      setError('Say which site this is.');
      return;
    }

    setSaving(true);
    setError(undefined);
    try {
      await DeliveryRequestsApi.correctAddress({
        uid: problem.requestUid,
        at: problem.at === 'destination' ? 'destination' : 'collection',
        site: site.trim(),
        location: location.trim(),
        postcode: postcode.trim(),
      });
      await onFixed();
    } catch (caught) {
      // Shown here rather than as a flag: "that address could not be found"
      // belongs next to the box it was typed into.
      setError((caught as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className='mt-2 rounded border bg-white p-3' data-testid='fix-address'>
      <p className='body-01 mb-2 text-gray-600'>
        {problem.at === 'destination' ? 'Where should this go?' : 'Where is this collected from?'}
      </p>

      <div className='flex flex-col gap-2'>
        <Textfield
          placeholder='Site — a hospital name, or anywhere else'
          value={site}
          testId='fix-site'
          onChange={(event) => setSite((event.target as HTMLInputElement).value)}
        />
        <Textfield
          placeholder='Ward, department or street address'
          value={location}
          testId='fix-location'
          onChange={(event) => setLocation((event.target as HTMLInputElement).value)}
        />
        <Textfield
          placeholder='Postcode'
          value={postcode}
          testId='fix-postcode'
          onChange={(event) => setPostcode((event.target as HTMLInputElement).value)}
        />
      </div>

      {error && (
        <p className='body-01 mt-2 text-red-600' data-testid='fix-address-error'>
          {error}
        </p>
      )}

      <div className='mt-3 flex gap-2'>
        <SharedButton
          type='button'
          spacing='compact'
          appearance='primary'
          label='Save and re-route'
          testId='save-address'
          isLoading={saving}
          onClick={save}
        />
        <SharedButton type='button' spacing='compact' appearance='subtle' label='Cancel' onClick={onCancel} />
      </div>
    </div>
  );
};

export default FixAddress;
